import React, { useState, useEffect } from 'react';
import {
    TrendingUp, TrendingDown, Minus, Search, AlertCircle,
    Bug, ChevronDown, ChevronUp,
} from 'lucide-react';
import { adminAnalytics } from '../../services/api';

/**
 * DecisionSummaryBanner
 *
 * Period-over-period decision layer for the Overview tab.
 * Reads from GET /internal/analytics/decision-summary.
 *
 * Shows:
 * - Improved / Degraded / Stable signal counts
 * - Per-metric trend arrows
 * - "Investigate Next" recommendation if any metric degraded
 * - Open bug count
 *
 * Fails silently (returns null) if backend unavailable.
 */

const STATUS_CONFIG = {
    improved:           { icon: TrendingUp,   color: '#10b981', bg: '#f0fdf4', border: '#bbf7d0', label: 'Improved'   },
    degraded:           { icon: TrendingDown,  color: '#ef4444', bg: '#fef2f2', border: '#fecaca', label: 'Degraded'   },
    stable:             { icon: Minus,         color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb', label: 'Stable'     },
    insufficient_data:  { icon: Minus,         color: '#9ca3af', bg: '#f9fafb', border: '#f3f4f6', label: 'No prior'   },
};

const METRIC_LABELS = {
    active_users:      'Active Users',
    lessons_generated: 'Lessons Generated',
    pipeline_health:   'Pipeline Health',
};

const DecisionSummaryBanner = ({ filters = {} }) => {
    const [summary, setSummary] = useState(null);
    const [error, setError] = useState(false);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const params = {
                    days: filters.days || 7,
                    ...(filters.district_id && { district_id: filters.district_id }),
                    ...(filters.campus_id && { campus_id: filters.campus_id }),
                };
                const res = await adminAnalytics.getDecisionSummary(params);
                if (!cancelled) setSummary(res.data);
            } catch {
                if (!cancelled) setError(true);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [filters.days, filters.district_id, filters.campus_id]);

    // Silent fail — banner is supplemental, not blocking
    if (error || !summary) return null;

    const signals = summary.signals || {};
    const counts = summary.summary_counts || {};
    const hasSignals = Object.keys(signals).length > 0;
    if (!hasSignals) return null;

    const degradedCount = counts.degraded || 0;
    const improvedCount = counts.improved || 0;
    const openBugs = summary.open_bugs || 0;
    const investigateNext = summary.investigate_next;
    const investigateReason = summary.investigate_reason;

    const bannerBg = degradedCount > 0 ? '#fef9f0' : improvedCount > 0 ? '#f0fdf4' : '#f8fafc';
    const bannerBorder = degradedCount > 0 ? '#fed7aa' : improvedCount > 0 ? '#bbf7d0' : '#e2e8f0';

    return (
        <div style={{
            background: bannerBg,
            border: `1px solid ${bannerBorder}`,
            borderRadius: 12,
            padding: '14px 18px',
            marginBottom: 24,
        }}>
            {/* Header row */}
            <div style={{
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        {summary.current_period_label || 'Last 7d'} vs {summary.prior_period_label || 'Prior 7d'}
                    </span>
                    <SummaryPill count={improvedCount} color="#10b981" label="Improved" />
                    <SummaryPill count={degradedCount} color="#ef4444" label="Degraded" />
                    <SummaryPill count={counts.stable || 0} color="#9ca3af" label="Stable" />
                    {openBugs > 0 && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#ef4444', fontWeight: 600 }}>
                            <Bug size={12} /> {openBugs} open bug{openBugs !== 1 ? 's' : ''}
                        </span>
                    )}
                </div>
                <button
                    onClick={() => setExpanded(v => !v)}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        fontSize: 12, color: '#64748b', background: 'none',
                        border: 'none', cursor: 'pointer',
                    }}
                >
                    {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    {expanded ? 'Less' : 'Detail'}
                </button>
            </div>

            {/* Investigate next hint */}
            {investigateNext && (
                <div style={{
                    display: 'flex', alignItems: 'flex-start', gap: 8,
                    marginTop: 10, padding: '8px 12px',
                    background: '#fffbeb', border: '1px solid #fde68a',
                    borderRadius: 8,
                }}>
                    <Search size={14} style={{ color: '#d97706', marginTop: 1, flexShrink: 0 }} />
                    <div>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#92400e' }}>
                            Investigate: {(METRIC_LABELS[investigateNext] || investigateNext).replace(/_/g, ' ')}
                        </span>
                        {investigateReason && (
                            <span style={{ fontSize: 12, color: '#b45309', marginLeft: 6 }}>
                                — {investigateReason}
                            </span>
                        )}
                    </div>
                </div>
            )}

            {/* Expanded: per-metric detail */}
            {expanded && (
                <div style={{
                    display: 'flex', gap: 10, flexWrap: 'wrap',
                    marginTop: 12, paddingTop: 12,
                    borderTop: '1px solid #e5e7eb',
                }}>
                    {Object.entries(signals).map(([key, sig]) => (
                        <MetricSignalChip
                            key={key}
                            label={METRIC_LABELS[key] || key}
                            status={sig.status}
                            delta={sig.delta_pct}
                            current={sig.current}
                            prior={sig.prior}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};


const SummaryPill = ({ count, color, label }) => {
    if (count === 0) return null;
    return (
        <span style={{
            fontSize: 11, fontWeight: 700, padding: '2px 8px',
            borderRadius: 20, background: `${color}18`,
            color, border: `1px solid ${color}30`,
        }}>
            {count} {label}
        </span>
    );
};


const MetricSignalChip = ({ label, status, delta, current, prior }) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.stable;
    const Icon = cfg.icon;
    const deltaStr = delta != null ? (delta > 0 ? `+${delta}%` : `${delta}%`) : null;

    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 12px', borderRadius: 8,
            background: cfg.bg, border: `1px solid ${cfg.border}`,
            fontSize: 12,
        }}>
            <Icon size={13} style={{ color: cfg.color }} />
            <span style={{ color: '#374151', fontWeight: 500 }}>{label}</span>
            {deltaStr && (
                <span style={{ color: cfg.color, fontWeight: 700 }}>{deltaStr}</span>
            )}
            {current != null && prior != null && (
                <span style={{ color: '#9ca3af', fontSize: 11 }}>
                    ({prior} → {current})
                </span>
            )}
            {status === 'insufficient_data' && (
                <span style={{ color: '#9ca3af', fontSize: 11 }}>No prior data</span>
            )}
        </div>
    );
};


export default DecisionSummaryBanner;
